import {CopyIcon, ScreenFullIcon} from '@primer/octicons-react'
import {Box, IconButton} from '@primer/react'
import type {DialogHeaderProps} from '@primer/react/experimental'
import {Dialog} from '@primer/react/experimental'
import {type RefObject, useCallback, useMemo} from 'react'

import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import {choosePathForRepository, issueNewPathFromExisting} from '../utils/urls'
import {noop} from '@github-ui/noop'
import {useKeyPress} from '@github-ui/use-key-press'
import {HOTKEYS} from '../constants/hotkeys'
import {LABELS} from '../constants/labels'
import {BUTTON_LABELS} from '../constants/buttons'
import type {IssueCreateUrlParams} from '../utils/template-args'
import {useIssueCreateConfigContext} from '../contexts/IssueCreateConfigContext'
import {DisplayMode} from '../utils/display-mode'
import {useIssueCreateDataContext} from '../contexts/IssueCreateDataContext'

type CreateIssueDialogHeaderProps = {
  createIssueUrlParamsRef?: RefObject<IssueCreateUrlParams>
  navigate: (url: string) => void
} & DialogHeaderProps

export const CreateIssueDialogHeader = ({
  createIssueUrlParamsRef,
  navigate,
  dialogLabelId,
  onClose,
}: CreateIssueDialogHeaderProps) => {
  const {displayMode, optionConfig} = useIssueCreateConfigContext()
  const {repository} = useIssueCreateDataContext()

  const onCloseClick = useCallback(() => {
    onClose('close-button')
  }, [onClose])

  const pathname = ssrSafeWindow?.location?.pathname ?? ''
  const origin = ssrSafeWindow?.location?.origin ?? ''
  const repoNwo = repository?.nameWithOwner ?? ''

  const baseUrlForFullscreenLink = useMemo(() => issueNewPathFromExisting(pathname), [pathname])
  const chooseUrlForCopy = useMemo(() => origin + choosePathForRepository(repoNwo), [origin, repoNwo])

  // For template selection, we want to show a "Copy URL" button, while for issue creation we can show a fullscreen navigation.
  const showCopyUrlButton = displayMode === DisplayMode.TemplatePicker && origin !== '' && repoNwo !== ''
  const showFullScreenButton =
    displayMode === DisplayMode.IssueCreation && optionConfig.showFullScreenButton && navigate !== noop

  const navigateToFullscreen = () => {
    if (!showFullScreenButton) {
      return
    }

    const templateParams = createIssueUrlParamsRef?.current?.generateUrlParams()
    const link = templateParams ? `${baseUrlForFullscreenLink}?${templateParams}` : baseUrlForFullscreenLink
    navigate(link)
  }

  useKeyPress([HOTKEYS.enter], navigateToFullscreen, {
    metaKey: true,
    shiftKey: true,
    triggerWhenPortalIsActive: true,
  })

  return (
    <Dialog.Header>
      <Box sx={{display: 'flex'}}>
        <Box sx={{display: 'flex', flexDirection: 'column', flexGrow: 1, px: 2, py: '6px'}}>
          <Dialog.Title id={dialogLabelId}>{LABELS.issueCreateDialogTitle}</Dialog.Title>
        </Box>
        {showCopyUrlButton && (
          <CopyToClipboardButton
            sx={{pt: '2px', borderRadius: '4px'}}
            textToCopy={chooseUrlForCopy}
            ariaLabel={BUTTON_LABELS.copyUrl}
            icon={CopyIcon}
            hasPortalTooltip={true}
            tooltipProps={{direction: 's'}}
          />
        )}
        {showFullScreenButton && (
          <IconButton
            sx={{pt: '2px', borderRadius: '4px'}}
            variant="invisible"
            onClick={navigateToFullscreen}
            aria-label={BUTTON_LABELS.createFullscreen}
            icon={ScreenFullIcon}
          />
        )}
        <Dialog.CloseButton onClose={onCloseClick} />
      </Box>
    </Dialog.Header>
  )
}

try{ CreateIssueDialogHeader.displayName ||= 'CreateIssueDialogHeader' } catch {}